import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import toast from 'react-hot-toast'
import { orderApi } from '../../services/api'
import Button from '../../components/common/Button'

/**
 * FillOrder — dedicated full-page packing view for ONE order.
 *
 * Has TWO modes depending on whether the order has a pending backorder slip:
 *
 *   1. NORMAL PACK MODE (no pending backorder slip)
 *      Shows the order's items as a big tappable checklist with:
 *        - Check off each item as you pull it from the shelf
 *        - "Short pack" link per line for shortages discovered mid-pack
 *        - Backordered items shown separately (informational)
 *        - Buttons: Print Slip + Download + Mark Packed
 *      Mark Packed transitions status to 'packed' and returns to the list.
 *
 *   2. BACKORDER PACK MODE (a backorder receiving batch is waiting)
 *      Shows ONLY the items in the selected batch (fetched from server):
 *        - Banner at top explaining what this is
 *        - If multiple pending batches, a picker to choose which one
 *        - Big tappable checklist of just the batch items
 *        - Button: Print Backorder Slip (prints + marks as printed, then goes back)
 *        - No "Mark Packed" — the main order status doesn't change
 *
 *   Important: batch items come from the backorder_fills table via
 *   listBackorderBatchItems — NOT from order.items. The latter has
 *   already-decremented backorder_quantity once fills are recorded.
 */
export default function FillOrder() {
    const { id } = useParams()
    const navigate = useNavigate()

    const [order, setOrder] = useState(null)
    const [loading, setLoading] = useState(true)
    const [busy, setBusy] = useState(false)
    const [checks, setChecks] = useState({})                  // { fillId/itemId: bool }
    const [shortPackTarget, setShortPackTarget] = useState(null)

    // Backorder slip pack mode
    const [pendingBatches, setPendingBatches] = useState([])
    const [selectedBatchId, setSelectedBatchId] = useState(null)
    const [batchItems, setBatchItems] = useState([])
    const [loadingBatchItems, setLoadingBatchItems] = useState(false)

    useEffect(() => { load() }, [id])

    async function load() {
        setLoading(true)
        try {
            const [orderRes, pendingRes] = await Promise.all([
                orderApi.getById(id),
                orderApi.listPendingBackorderSlipsForOrder(id),
            ])

            if (orderRes.success) setOrder(orderRes.order)
            else toast.error(orderRes.message || 'Could not load order')

            if (pendingRes.success) {
                const batches = pendingRes.pending || []
                setPendingBatches(batches)
                if (batches.length > 0) {
                    setSelectedBatchId(batches[0].batchId)
                }
            }
        } catch {
            toast.error('Network error')
        } finally {
            setLoading(false)
        }
    }

    // When the selected batch changes, fetch items from server.
    // (We can't derive these from order.items because order_items.backorder_quantity
    //  gets decremented when the fill is created.)
    useEffect(() => {
        if (selectedBatchId == null) {
            setBatchItems([])
            return
        }
        if (!order) return

        let cancelled = false
        setLoadingBatchItems(true)
        setChecks({})
        orderApi.listBackorderBatchItems(order.id, selectedBatchId)
            .then(response => {
                if (cancelled) return
                if (response.success) {
                    setBatchItems(response.items || [])
                } else {
                    toast.error(response.message || 'Could not load batch items')
                    setBatchItems([])
                }
            })
            .catch(() => {
                if (cancelled) return
                toast.error('Network error loading batch items')
                setBatchItems([])
            })
            .finally(() => {
                if (!cancelled) setLoadingBatchItems(false)
            })

        return () => { cancelled = true }
    }, [selectedBatchId, order])

    function toggleCheck(key) {
        setChecks(c => ({ ...c, [key]: !c[key] }))
    }

    async function handleMarkPacked() {
        if (!confirm(`Mark order #${order.id} as packed? This will open the packing slip to print.`)) return

        // Open the print tab now, synchronously, so the popup blocker allows it.
        // We'll point it at the PDF once the slip is ready. If the browser blocks
        // it anyway, `win` is null and we fall back to a normal open below.
        const win = window.open('', '_blank')

        setBusy(true)
        try {
            const response = await orderApi.updateStatus(order.id, { status: 'packed' })
            if (!response.success) {
                if (win) win.close()
                toast.error(response.message || 'Update failed')
                return
            }

            // Now that it's packed, the slip renders with Filled / Backorder filled in.
            try {
                const { blob } = await orderApi.fetchPackingSlip(order.id, { inline: true })
                const url = URL.createObjectURL(blob)
                if (win) win.location = url
                else window.open(url, '_blank')   // fallback if the pre-open was blocked
                setTimeout(() => URL.revokeObjectURL(url), 60_000)
            } catch {
                if (win) win.close()
                toast.error('Order packed, but the slip could not be opened — use Print Slip.')
            }

            toast.success(`Order #${order.id} packed`)
            await load()   // refresh in place so the page shows the packed state; no bounce
        } catch {
            if (win) win.close()
            toast.error('Network error')
        } finally {
            setBusy(false)
        }
    }

    async function handlePreviewSlip() {
        try {
            const { blob } = await orderApi.fetchPackingSlip(order.id, { inline: true })
            const url = URL.createObjectURL(blob)
            window.open(url, '_blank')
            setTimeout(() => URL.revokeObjectURL(url), 60_000)
        } catch {
            toast.error('Could not load packing slip')
        }
    }

    async function handleDownloadSlip() {
        try {
            const { blob, fileName } = await orderApi.fetchPackingSlip(order.id, { inline: false })
            const url = URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.href = url
            a.download = fileName
            document.body.appendChild(a)
            a.click()
            document.body.removeChild(a)
            URL.revokeObjectURL(url)
        } catch {
            toast.error('Could not download packing slip')
        }
    }

    async function handlePrintBackorderSlip() {
        if (!selectedBatchId) return
        setBusy(true)
        try {
            const { blob } = await orderApi.fetchBackorderSlip(order.id, selectedBatchId, { inline: true })
            const url = URL.createObjectURL(blob)
            window.open(url, '_blank')
            setTimeout(() => URL.revokeObjectURL(url), 60_000)

            const result = await orderApi.markBackorderSlipPrinted(order.id, selectedBatchId)
            if (result.success) {
                toast.success('Backorder slip printed')
                setTimeout(() => navigate('/admin/fulfillment'), 1500)
            } else {
                toast.error(result.message || 'Slip generated but could not mark as printed')
            }
        } catch (err) {
            toast.error(err?.message || 'Could not print backorder slip')
        } finally {
            setBusy(false)
        }
    }

    async function handleDownloadBackorderSlip() {
        if (!selectedBatchId) return
        try {
            const { blob, fileName } = await orderApi.fetchBackorderSlip(order.id, selectedBatchId, { inline: false })
            const url = URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.href = url
            a.download = fileName
            document.body.appendChild(a)
            a.click()
            document.body.removeChild(a)
            URL.revokeObjectURL(url)
        } catch {
            toast.error('Could not download backorder slip')
        }
    }

    if (loading) {
        return <div className="p-8 text-center text-gray-500">Loading order...</div>
    }

    if (!order) {
        return (
            <div className="bg-white rounded-xl shadow-sm p-8 text-center max-w-xl mx-auto">
                <h2 className="text-xl font-semibold text-ghasc-navy">Order not found</h2>
                <div className="mt-4">
                    <Link to="/admin/fulfillment"><Button>Back to Fulfillment</Button></Link>
                </div>
            </div>
        )
    }

    const isPacked = order.status === 'packed'
    const isPickedUp = order.status === 'picked_up'
    const isBackorderMode = pendingBatches.length > 0
    const selectedBatch = pendingBatches.find(b => b.batchId === selectedBatchId)

    // -----------------------------------------------------------------------
    //  BACKORDER PACK MODE
    // -----------------------------------------------------------------------
    if (isBackorderMode) {
        const totalChecked = batchItems.filter(i => checks[i.fillId]).length
        const allChecked = batchItems.length > 0 && totalChecked === batchItems.length

        return (
            <div className="space-y-4 max-w-3xl mx-auto">
                {/* Header */}
                <div className="flex items-start justify-between flex-wrap gap-2">
                    <div>
                        <h2 className="text-2xl font-semibold text-ghasc-navy">
                            Backorder Delivery — Order #{order.id}
                        </h2>
                        <p className="text-sm text-gray-700 mt-1">
                            <strong>{order.customerName}</strong>
                        </p>
                    </div>
                    <Link to="/admin/fulfillment">
                        <Button variant="secondary">← Back</Button>
                    </Link>
                </div>

                {/* Notice banner */}
                <div className="bg-amber-50 border-l-4 border-ghasc-tan rounded-r-xl p-4">
                    <div className="text-amber-900 font-semibold">📦 Backorder Ready</div>
                    <p className="text-sm text-amber-800 mt-1">
                        Items from a regional shipment are ready to deliver to this home group.
                        Pull these items, print the backorder slip, and bring them to the next area meeting.
                    </p>
                </div>

                {/* Batch picker (if multiple) */}
                {pendingBatches.length > 1 && (
                    <div className="bg-white rounded-xl shadow-sm p-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Multiple backorder batches pending — which one are you delivering?
                        </label>
                        <div className="space-y-2">
                            {pendingBatches.map(b => (
                                <label key={b.batchId} className="flex items-center gap-3 px-3 py-2 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
                                    <input
                                        type="radio"
                                        name="batch"
                                        checked={selectedBatchId === b.batchId}
                                        onChange={() => setSelectedBatchId(b.batchId)}
                                        className="w-4 h-4"
                                    />
                                    <div className="flex-1">
                                        <div className="text-sm font-medium text-gray-900">
                                            Received {new Date(b.batchReceivedAt).toLocaleDateString('en-US', {
                                                month: 'long', day: 'numeric', year: 'numeric',
                                            })}
                                        </div>
                                        <div className="text-xs text-gray-500">
                                            {b.lineCount} item{b.lineCount === 1 ? '' : 's'} · {b.totalUnits} unit{b.totalUnits === 1 ? '' : 's'}
                                        </div>
                                    </div>
                                </label>
                            ))}
                        </div>
                    </div>
                )}

                {/* Selected batch info */}
                {selectedBatch && (
                    <div className="bg-white rounded-xl shadow-sm p-4 flex items-center justify-between flex-wrap gap-2">
                        <div>
                            <div className="text-xs uppercase tracking-wider text-gray-500">Batch received</div>
                            <div className="font-medium text-gray-900">
                                {new Date(selectedBatch.batchReceivedAt).toLocaleDateString('en-US', {
                                    weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
                                })}
                            </div>
                        </div>
                        {allChecked && (
                            <span className="inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                ✓ All items pulled
                            </span>
                        )}
                    </div>
                )}

                {/* Items to deliver */}
                {loadingBatchItems ? (
                    <div className="bg-white rounded-xl shadow-sm p-6 text-center text-gray-500">
                        Loading batch items...
                    </div>
                ) : batchItems.length === 0 ? (
                    <div className="bg-white rounded-xl shadow-sm p-6 text-center text-gray-500">
                        No items to display.
                    </div>
                ) : (
                    <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                        <div className="px-4 py-3 border-b border-gray-100">
                            <h3 className="text-sm font-semibold text-ghasc-navy uppercase tracking-wider">
                                Pull from shelf
                            </h3>
                        </div>
                        <ul className="divide-y divide-gray-100">
                            {batchItems.map(item => {
                                const checked = !!checks[item.fillId]
                                const variantLabel = [
                                    item.year,
                                    item.medallionType === 'Triplate' && item.colorOrderCode
                                        ? item.colorOrderCode
                                        : item.color,
                                ].filter(Boolean).join(' / ')
                                return (
                                    <li key={item.fillId}
                                        className={`flex items-center gap-3 px-4 py-3 ${checked ? 'bg-green-50' : ''}`}>
                                        <button
                                            type="button"
                                            onClick={() => toggleCheck(item.fillId)}
                                            className={`flex-shrink-0 w-12 h-12 rounded-lg border-2 flex items-center justify-center text-2xl ${checked
                                                ? 'bg-green-500 border-green-500 text-white'
                                                : 'border-gray-300 bg-white text-transparent hover:border-ghasc-navy'
                                                }`}
                                            aria-label={`Check ${item.description}`}
                                        >
                                            ✓
                                        </button>
                                        <div className="flex-shrink-0 text-3xl font-bold text-ghasc-navy w-12 text-center">
                                            {item.quantity}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="text-sm font-medium text-gray-900 break-words">
                                                {item.description}
                                            </div>
                                            <div className="text-xs text-gray-500 font-mono mt-0.5">
                                                {item.itemNumber}
                                                {variantLabel && <span className="ml-2 font-sans">— {variantLabel}</span>}
                                            </div>
                                        </div>
                                    </li>
                                )
                            })}
                        </ul>
                    </div>
                )}

                {/* Action buttons */}
                <div className="bg-white rounded-xl shadow-sm p-4 flex flex-wrap gap-2">
                    <button
                        onClick={handlePrintBackorderSlip}
                        disabled={busy || batchItems.length === 0}
                        className="flex-1 min-w-[160px] px-3 py-3 text-sm font-medium text-white bg-ghasc-navy rounded-lg hover:opacity-90 disabled:opacity-50"
                    >
                        🖨 Print Backorder Slip
                    </button>
                    <button
                        onClick={handleDownloadBackorderSlip}
                        disabled={busy || batchItems.length === 0}
                        className="px-3 py-3 text-sm font-medium text-ghasc-navy bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50"
                        title="Download as PDF file"
                    >
                        ⬇ Download
                    </button>
                </div>

                <div className="text-xs text-gray-500 text-center pt-2">
                    Printing will mark this slip as done so it no longer shows on Fulfillment.
                </div>
            </div>
        )
    }

    // -----------------------------------------------------------------------
    //  NORMAL PACK MODE
    // -----------------------------------------------------------------------
    const packLines = order.items.filter(i => i.filledQuantity > 0)
    const backorderLines = order.items.filter(i => i.backorderQuantity > 0)
    const totalChecked = packLines.filter(i => checks[i.id]).length
    const allChecked = packLines.length > 0 && totalChecked === packLines.length

    return (
        <div className="space-y-4 max-w-3xl mx-auto">
            {/* Header */}
            <div className="flex items-start justify-between flex-wrap gap-2">
                <div>
                    <h2 className="text-2xl font-semibold text-ghasc-navy">
                        Fill Order #{order.id}
                    </h2>
                    <p className="text-sm text-gray-700 mt-1">
                        <strong>{order.customerName}</strong>
                    </p>
                    <p className="text-xs text-gray-500">
                        Submitted by {order.submitterName} · {new Date(order.createdAt).toLocaleDateString('en-US', {
                            month: 'short', day: 'numeric', year: 'numeric',
                        })}
                    </p>
                </div>
                <Link to="/admin/fulfillment">
                    <Button variant="secondary">← Back</Button>
                </Link>
            </div>

            {/* Already-packed banner */}
            {isPacked && (
                <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4 text-sm text-indigo-900">
                    <div className="font-semibold">This order has already been packed.</div>
                    <p className="mt-1">You can still print a packing slip or mark items as short-packed if needed.</p>
                </div>
            )}

            {/* Already-picked-up banner */}
            {isPickedUp && (
                <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-sm text-green-900">
                    <div className="font-semibold">This order has been delivered.</div>
                    <p className="mt-1">You can still print the packing slip for reference.</p>
                </div>
            )}

            {/* Progress summary */}
            {!isPacked && !isPickedUp && packLines.length > 0 && (
                <div className="bg-white rounded-xl shadow-sm p-4 flex items-center justify-between flex-wrap gap-2">
                    <div className="text-sm text-gray-700">
                        Items checked: <strong>{totalChecked}</strong> of {packLines.length}
                    </div>
                    {allChecked && (
                        <span className="inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            ✓ All items pulled
                        </span>
                    )}
                </div>
            )}

            {/* Items to pack */}
            {packLines.length === 0 ? (
                <div className="bg-white rounded-xl shadow-sm p-6 text-center text-gray-500">
                    <p className="font-medium text-gray-700">Nothing to pack</p>
                    <p className="text-xs mt-1">All items on this order are backordered.</p>
                </div>
            ) : (
                <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                    <div className="px-4 py-3 border-b border-gray-100">
                        <h3 className="text-sm font-semibold text-ghasc-navy uppercase tracking-wider">
                            Pull from shelf
                        </h3>
                    </div>
                    <ul className="divide-y divide-gray-100">
                        {packLines.map(item => {
                            const checked = !!checks[item.id]
                            const variantLabel = [
                                item.year,
                                item.medallionType === 'Triplate' && item.colorOrderCode
                                    ? item.colorOrderCode
                                    : item.color,
                            ].filter(Boolean).join(' / ')
                            return (
                                <li key={item.id}
                                    className={`flex items-center gap-3 px-4 py-3 ${checked ? 'bg-green-50' : ''}`}>
                                    <button
                                        type="button"
                                        onClick={() => toggleCheck(item.id)}
                                        disabled={isPacked || isPickedUp}
                                        className={`flex-shrink-0 w-12 h-12 rounded-lg border-2 flex items-center justify-center text-2xl ${checked
                                            ? 'bg-green-500 border-green-500 text-white'
                                            : 'border-gray-300 bg-white text-transparent hover:border-ghasc-navy'
                                            } disabled:opacity-40`}
                                        aria-label={`Check ${item.description}`}
                                    >
                                        ✓
                                    </button>
                                    <div className="flex-shrink-0 text-3xl font-bold text-ghasc-navy w-12 text-center">
                                        {item.filledQuantity}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm font-medium text-gray-900 break-words">
                                            {item.description}
                                        </div>
                                        <div className="text-xs text-gray-500 font-mono mt-0.5">
                                            {item.itemNumber}
                                            {variantLabel && <span className="ml-2 font-sans">— {variantLabel}</span>}
                                        </div>
                                    </div>
                                    {!isPacked && !isPickedUp && (
                                        <button
                                            type="button"
                                            onClick={() => setShortPackTarget(item)}
                                            className="flex-shrink-0 px-3 py-2 text-xs text-amber-700 bg-amber-50 rounded-lg hover:bg-amber-100"
                                            title="Found fewer than the system said?"
                                        >
                                            Short<br />pack
                                        </button>
                                    )}
                                </li>
                            )
                        })}
                    </ul>
                </div>
            )}

            {/* Backordered items */}
            {backorderLines.length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm">
                    <div className="font-semibold text-amber-900 mb-2">
                        Backordered — will ship separately when received
                    </div>
                    <ul className="space-y-1 text-amber-900">
                        {backorderLines.map(item => (
                            <li key={item.id} className="flex items-baseline gap-2">
                                <span className="font-bold text-lg">{item.backorderQuantity}</span>
                                <span>×</span>
                                <span className="flex-1">
                                    {item.description}
                                    {(item.year || item.color) && (
                                        <span className="text-amber-700 text-xs ml-1">
                                            ({[item.year, item.color].filter(Boolean).join(' / ')})
                                        </span>
                                    )}
                                </span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {/* Action buttons */}
            <div className="bg-white rounded-xl shadow-sm p-4 flex flex-wrap gap-2">
                <button
                    onClick={handlePreviewSlip}
                    className="flex-1 min-w-[140px] px-3 py-3 text-sm font-medium text-white bg-ghasc-navy rounded-lg hover:opacity-90"
                >
                    🖨 Print Slip
                </button>
                <button
                    onClick={handleDownloadSlip}
                    className="px-3 py-3 text-sm font-medium text-ghasc-navy bg-gray-100 rounded-lg hover:bg-gray-200"
                    title="Download as PDF file"
                >
                    ⬇ Download
                </button>
                {!isPacked && !isPickedUp && (
                    <Button
                        onClick={handleMarkPacked}
                        loading={busy}
                        className="flex-1 min-w-[160px]"
                    >
                        Mark Packed
                    </Button>
                )}
            </div>

            {/* Short-pack modal */}
            {shortPackTarget && (
                <ShortPackModal
                    order={order}
                    item={shortPackTarget}
                    onClose={() => setShortPackTarget(null)}
                    onSaved={() => { setShortPackTarget(null); load() }}
                />
            )}
        </div>
    )
}

// ============================================================================
//  ShortPackModal
// ============================================================================
function ShortPackModal({ order, item, onClose, onSaved }) {
    const previousFilled = item.filledQuantity
    const [actualFilled, setActualFilled] = useState(String(previousFilled))
    const [busy, setBusy] = useState(false)

    const parsed = parseInt(actualFilled, 10)
    const isValid = !isNaN(parsed) && parsed >= 0 && parsed < previousFilled
    const shortage = isNaN(parsed) ? 0 : Math.max(0, previousFilled - parsed)

    async function handleSubmit(e) {
        e.preventDefault()
        if (!isValid) {
            toast.error(`Actual found must be 0 to ${previousFilled - 1}`)
            return
        }
        setBusy(true)
        try {
            const response = await orderApi.shortPack(order.id, item.id, parsed)
            if (response.success) {
                toast.success(`Short-packed: ${parsed} actual, ${shortage} added to backorder`)
                onSaved()
            } else {
                toast.error(response.message || 'Short-pack failed')
            }
        } catch {
            toast.error('Network error')
        } finally {
            setBusy(false)
        }
    }

    return (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
            <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-xl w-full max-w-md">
                <div className="p-6 space-y-4">
                    <h3 className="text-lg font-semibold text-ghasc-navy">Short Pack</h3>
                    <div className="bg-gray-50 rounded-lg p-3 text-sm space-y-1">
                        <div className="font-medium text-gray-900">{item.description}</div>
                        <div className="text-xs text-gray-500 font-mono">{item.itemNumber}</div>
                        <div className="text-sm text-gray-700 pt-1">
                            <strong>System said:</strong> {previousFilled} filled
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            How many did you actually find?
                        </label>
                        <input
                            type="number"
                            inputMode="numeric"
                            min="0"
                            max={previousFilled - 1}
                            value={actualFilled}
                            onChange={(e) => setActualFilled(e.target.value)}
                            autoFocus
                            className="no-spinner w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-ghasc-navy text-lg"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                            Enter a number from 0 to {previousFilled - 1}.
                        </p>
                    </div>

                    {isValid && (
                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-900">
                            <div><strong>{parsed}</strong> will be packed now.</div>
                            <div><strong>{shortage}</strong> will move to backorder.</div>
                        </div>
                    )}

                    <div className="flex justify-end gap-2 pt-2">
                        <Button type="button" variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
                        <Button type="submit" loading={busy} disabled={!isValid}>Confirm</Button>
                    </div>
                </div>
            </form>
        </div>
    )
}